import { type Aspect, ASPECTS, type BackcountryMetadata } from "~/lib/api";
import { formatMonthRange } from "~/lib/dateUtils";
import { cn } from "~/lib/utils";
import { Icon } from "~/components/ui/icons/icon";
import { DifficultyBadge } from "../DifficultySelector";
import { DistanceDisplay } from "../DistanceEditor";
import { DurationDisplay } from "../DurationEditor";
import { ElevationDisplay } from "../ElevationEditor";
import { StatsLine } from "../StatsLine";

interface BackcountryStatsProps {
  metadata: BackcountryMetadata | null | undefined;
  compact?: boolean;
  outline?: boolean;
  className?: string;
}

export function BackcountryStats({ metadata, compact, outline, className }: BackcountryStatsProps) {
  if (!metadata) return null;

  const { difficulty, distance, elevation, duration, aspect, bestMonths } = metadata;

  const hasElevation = !!(elevation && (elevation.gain > 0 || elevation.loss > 0));
  const hasAspect = aspect && aspect.length > 0;
  const hasSeason = bestMonths && bestMonths.length > 0;
  const hasStats = difficulty || distance || hasElevation || duration || hasAspect || hasSeason;
  const hasConditions = hasAspect || hasSeason;

  if (!hasStats) return null;

  const ariaLabel = buildAriaLabel({ difficulty, distance, elevation, duration, aspect, bestMonths, hasElevation });

  const coreStats = (
    <>
      {difficulty && <DifficultyBadge difficulty={difficulty} outline={outline} />}
      {distance && <DistanceDisplay distance={distance} />}
      {hasElevation && <ElevationDisplay elevation={elevation} />}
      {duration && <DurationDisplay duration={duration} />}
    </>
  );

  if (compact) {
    return (
      <StatsLine className={className} aria-label={ariaLabel}>
        {coreStats}
      </StatsLine>
    );
  }

  return (
    <div className={cn("flex flex-col gap-0.5", className)} aria-label={ariaLabel}>
      <StatsLine>{coreStats}</StatsLine>
      {hasConditions && (
        <StatsLine>
          {hasAspect && <AspectDisplay aspects={aspect as Aspect[]} />}
          {hasSeason && <SeasonDisplay months={bestMonths} />}
        </StatsLine>
      )}
    </div>
  );
}

function AspectDisplay({ aspects }: { aspects: Aspect[] }) {
  const sorted = ASPECTS.filter((a) => aspects.includes(a));
  return (
    <span className="inline-flex items-center gap-1">
      <Icon i="compass" className="size-3.5" />
      {sorted.join(", ")}
    </span>
  );
}

function SeasonDisplay({ months }: { months: number[] }) {
  const text = formatMonthRange(months);
  return (
    <span className="inline-flex items-center gap-1">
      <Icon i="calendar" className="size-3.5" />
      {text}
    </span>
  );
}

interface AriaLabelProps {
  difficulty?: string;
  distance?: { value: number; unit: string };
  elevation?: { gain: number; loss: number; unit: string };
  duration?: { min: number; max: number; unit: string };
  aspect?: string[];
  bestMonths?: number[];
  hasElevation: boolean;
}

function buildAriaLabel({
  difficulty,
  distance,
  elevation,
  duration,
  aspect,
  bestMonths,
  hasElevation,
}: AriaLabelProps): string {
  const parts: string[] = [];

  if (difficulty) {
    parts.push(`${difficulty} difficulty`);
  }

  if (distance) {
    const unit = distance.unit === "km" ? "kilometers" : "miles";
    parts.push(`${distance.value} ${unit}`);
  }

  if (hasElevation && elevation) {
    const unit = elevation.unit === "m" ? "meters" : "feet";
    if (elevation.gain > 0 && elevation.loss > 0) {
      if (elevation.gain === elevation.loss) {
        parts.push(`${elevation.gain} ${unit} elevation gain and loss`);
      } else {
        parts.push(`${elevation.gain} ${unit} gain, ${elevation.loss} ${unit} loss`);
      }
    } else if (elevation.gain > 0) {
      parts.push(`${elevation.gain} ${unit} elevation gain`);
    } else if (elevation.loss > 0) {
      parts.push(`${elevation.loss} ${unit} elevation loss`);
    }
  }

  if (duration) {
    if (duration.min === duration.max) {
      parts.push(`${duration.min} hours`);
    } else {
      parts.push(`${duration.min} to ${duration.max} hours`);
    }
  }

  if (aspect && aspect.length > 0) {
    parts.push(`${aspect.join(", ")} facing`);
  }

  if (bestMonths && bestMonths.length > 0) {
    parts.push(`best season ${formatMonthRange(bestMonths)}`);
  }

  return `Backcountry stats: ${parts.join(", ")}`;
}
